import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDrag, useDrop, DragSourceMonitor, DropTargetMonitor } from 'react-dnd'
import { Key } from '@react-types/shared';

import { Listbox, ListboxItem, Chip, ScrollShadow, Avatar, Skeleton, Input } from "@heroui/react";

import { RoleContext } from '@/app/api/common';
import { ChannelList, ChannelListDescriptor, copyChannelListDescriptors, ChannelListManager, checkNoError, copyChannelList, getChannelListByEpgId, getField, setField, UrlInfo } from '@/features/channels/channelsSlice';
import { HotelRoom } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import { NumberText } from '@/components/Common/Field';
import FlexRow from '@/components/Common/FlexRow';

import { CatHeader } from './Project';

export interface ProjectListsProps {
  lists:				ChannelListDescriptor[];
  setLists:				(lists: ChannelListDescriptor[]) => any;

  sources:				ChannelListManager;
  room?:				HotelRoom | null;
}

const ProjectLists = (props: ProjectListsProps) => {
  const { t } = useTranslation();
  
  const role = React.useContext(RoleContext);
  
  const [selectedAdded, setSelectedAdded] = React.useState(new Set<Key>());
  const [selectedAvailable, setSelectedAvailable] = React.useState(new Set<Key>());
  const [searchAdded, setSearchAdded] = React.useState('');
  const [searchAvailable, setSearchAvailable] = React.useState('');

  console.log("ProjectLists/props=", props);
  
  const handleselectionChange = (vals: "all" | Set<Key>, setter: (val: Set<Key>) => any, unsetter: (val: Set<Key>) => any) => {
    console.log("handleselectionChange/vals=", vals);
    
    if (vals === "all")
      return
    
    const val = vals.size
				?	[[...vals][0]]
				:	[];
	
    console.log("handleselectionChange/val=", val);
    setter(new Set(val));
    unsetter(new Set([]));
  };
  
  const addChannelList = () => {
    const epg_id = selectedAvailable.values().next().value;
    console.log("addChannelList/epg_id=", epg_id);
    
    setSelectedAvailable(new Set([]));
    
    if (epg_id == "")
      return;
    
    const list = getChannelListByEpgId(String(epg_id), props.sources);
    console.log("addChannelList/epg_id=", epg_id + "/list=", list);
    if (!list)
      return;
    
    const desc: ChannelListDescriptor = {
      id:				list.epg_id,
      start_from:		0,
    };
    
    let descs = copyChannelListDescriptors(props.lists);
    descs.push(desc);
    
    props.setLists(descs);
  };
  
  const deleteChannelList = () => {
    const epg_id = selectedAdded.values().next().value;
    console.log("deleteChannelList/epg_id=" + epg_id);
    
    setSelectedAdded(new Set([]));
    
    if (epg_id == "")
      return;
    
    let descs = copyChannelListDescriptors(props.lists);
    let i = 0;
    
    while (i < descs.length) {
      const desc = descs[i];
      if (desc.id != epg_id) {
        i++;
        continue;
      }
      
      descs.splice(i, 1);
    }
    console.log("deleteChannelList/epg_id=" + epg_id + "/descs=", descs);
    
    props.setLists(descs);
  };
  
  const moveChannelList = (idx: number, move_by: number) => {
    let descs = copyChannelListDescriptors(props.lists);
    const list = descs.splice(idx, 1);
    
    descs.splice(idx + move_by, 0, list[0]);
    
    props.setLists(descs);
  };
  const adminOrPrivilage=(value:any)=>{
   return (role.admin || role?.privilages?.includes(value))
  }
  
  const lbLabel = t("projects:project:project_settings:channel_lists:" + (props.room ? "room_lists": "project_lists"));
  const lbEmpty = t("projects:project:project_settings:channel_lists:" + (props.room ? "room_empty": "project_empty"));

  const filteredAddedLists = props.lists.filter((desc) => {
    const list = getChannelListByEpgId(desc.id, props.sources);
    const name = list ? list.name.toLowerCase().replace(/\s+/g, "") : '';
    return name.includes(searchAdded.toLowerCase().replace(/\s+/g, ""));
  });

  const filteredAvailableLists = (props.sources.lists || []).filter((list) => {
    return list.name.toLowerCase().replace(/\s+/g, "").includes(searchAvailable.toLowerCase().replace(/\s+/g, ""));
  });
  
  return (
  <React.Fragment>
      <CatHeader caption="projects:project:project_settings:channel_lists:caption"
	             margin="mt-[8px]" />
	{props.sources.lists &&
	  <FlexRow className="w-[720px] gap-[12px]"
	           align="start">
	    <Listbox
          classNames={{
            base: "w-full border-gray-300 border-1",
            list: "min-h-[300px] max-h-[300px] min-w-[250px] overflow-y-auto",
          }}
          label={lbLabel}
          selectedKeys={selectedAdded}
          selectionMode="single"
          topContent={
            <div className="flex flex-col gap-0 p-2">
              <FlexRow className="m18 text-black"
                       justify="center">
                { lbLabel }
              </FlexRow>
              <Input
                placeholder={t("common:Search")}
                value={searchAdded}
                onValueChange={setSearchAdded}
                size="sm"
                isClearable
                startContent={<span className="material-icons text-gray-400 text-sm">search</span>}
              />
            </div>
          }
          items={filteredAddedLists}
          onSelectionChange={(vals) => handleselectionChange(vals, setSelectedAdded, setSelectedAvailable)}
          variant="faded"
          emptyContent={lbEmpty}>
        {filteredAddedLists.map((desc: ChannelListDescriptor) => {
          const list = getChannelListByEpgId(desc.id, props.sources);
          const originalIndex = props.lists.findIndex((d) => d.id === desc.id);
          if (
          		!list
          	 )
            return null;
          
          return (
          <ListboxItem key={desc.id}
                       textValue={list ? list.name : ''}>
			<ChanListItem desc={desc}
						  index={originalIndex}
						  items={props.lists}
						  sources={props.sources}
						  requestMove={moveChannelList} />
		  </ListboxItem>
		  );
        })}
        </Listbox>
        <FlexRow direction="column"
                 className="min-h-[300px] gap-[16px]"
                 justify="center">
          <Button caption=""
		          classNames={{
		            icon:	["text-[32px]", "text-bold"],
		          }}
		          isDisabled={selectedAvailable.size == 0}
		          icon="chevron_left"
		          layout="white"
		          onClick={() => selectedAvailable.size && addChannelList()} />
          <Button caption=""
		          classNames={{
		            icon:	["text-[32px]", "text-bold"],
		          }}
		          isDisabled={selectedAdded.size == 0}
		          icon="chevron_right"
		          layout="white"
		          onClick={() => selectedAdded.size && deleteChannelList()} />
        </FlexRow>
        <Listbox
          classNames={{
            base: "w-full border-gray-300 border-1",
            list: "min-h-[300px] max-h-[300px] min-w-[250px] overflow-y-auto",
          }}
          label={t("projects:project:project_settings:channel_lists:available_lists")}
          disabledKeys={props.lists.map((desc) => desc.id)}
          selectedKeys={selectedAvailable}
          selectionMode="single"
          topContent={
            <div className="flex flex-col gap-2 p-2">
              <FlexRow className="m18 text-black"
                       justify="center">
                { t("projects:project:project_settings:channel_lists:available_lists") }
              </FlexRow>
              <Input
                placeholder={t("common:Search")}
                value={searchAvailable}
                onValueChange={setSearchAvailable}
                size="sm"
                isClearable
                startContent={<span className="material-icons text-gray-400 text-sm">search</span>}
              />
            </div>
          }
          items={filteredAvailableLists}
          onSelectionChange={(vals) => handleselectionChange(vals, setSelectedAvailable, setSelectedAdded)}
          variant="faded">
          {(list: ChannelList) => (
            <ListboxItem key={list.epg_id}
                         textValue={list.name}
                         endContent={
                           <NumberText>
                             { list.channels ? list.channels.length : 0 }
                           </NumberText>
                         }
						 startContent={
						   <div className="material-icons !text-black">
							 list
						   </div>
						 }>
              {list.name}
            </ListboxItem>
          )}
        </Listbox>
	  </FlexRow>
	}
  </React.Fragment>
  );
};

export interface ChanListItemProps {
	desc:				ChannelListDescriptor;
	items:				ChannelListDescriptor[];
	sources:			ChannelListManager;
	requestMove:		(index: number, by: number) => any;
	index:				number;
}

interface DragCollect {
	isDragging:			boolean;
}

interface DropCollect {
	isOver: 			boolean;
	canDrop:			boolean;
}

export const ChanListItem = (props: ChanListItemProps) => {
  const list = getChannelListByEpgId(props.desc.id, props.sources);
  
  const [{ isDragging }, drag] = useDrag<ChanListItemProps, DragCollect, DragCollect>(() => ({
	collect: (monitor: DragSourceMonitor) => ({
	  isDragging: monitor.isDragging(),
	}),
	item: props,
	type: "chanlist_item",
  }),
  [props, list]);
  
  const dropTemplate = () => ({
	accept: "chanlist_item",
	canDrop: (item: ChanListItemProps) => {
	  return true;
	},
	collect: (monitor: DropTargetMonitor) => ({
	  isOver: monitor.isOver(),
	  canDrop: monitor.canDrop(),
	}),
	drop: (item: ChanListItemProps, monitor: DropTargetMonitor) => {
	  console.log("ChanListItem/useDrop/drop/item=", item, "/props=", props);
	  
	  if (	!item ||
	  		item.index == props.index
	  	 )
	    return undefined;
	  
	  const indexDiff = props.index - item.index;
	  const dropResult = monitor.getDropResult();
	  console.log("ChanListItem/useDrop/drop/item=", item, "/monitor=", monitor, "/props=", props, "/indexDiff=" + indexDiff, "/dropResult=", dropResult);
	  
	  console.log(`ChanListItem/useDrop/Calling item.requestMove(${item.index}, ${indexDiff})`);
	  item.requestMove(item.index, indexDiff);
	  return undefined;
	},
  });

  const [canDrop1, drop1] = useDrop<ChanListItemProps, DropCollect, DropCollect>(
	dropTemplate,
	[props.desc, props.index]
  );
  
  return (
  <FlexRow innerRef={drag}>
    <FlexRow innerRef={drop1}>
	  <FlexRow flex={0}>
		<div className="material-icons !text-black">
		  list
		</div>
	  </FlexRow>
	  <FlexRow className="pl-2">
		{list ? list.name : "-"}
	  </FlexRow>
	  <FlexRow flex={0} align="end">
	    <NumberText>
           { list && list.channels ? list.channels.length : 0 }
         </NumberText>
	  </FlexRow>
    </FlexRow>
  </FlexRow>
  );
}


export default ProjectLists;


